import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import CoursesService from '@/services/Quiz/quiz.service';
import { Quiz, QuizAttempt, QuizAttemptSubmission } from '@/shared/types/Quiz';


export const useQuizzes = (courseId: number, moduleId: number) => {
  const {
    data: quizzes,
    isLoading,
    isError,
    error,
  } = useQuery<Quiz[]>({
    queryKey: ['quizzes', courseId, moduleId],
    queryFn: ({ signal }) => CoursesService.getQuizzes(courseId, moduleId, signal),
    enabled: !!courseId && !!moduleId,
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

  return {
    quizzes: quizzes ?? [],
    isLoading,
    isError,
    error,
  };
};


export const useQuiz = (
  courseId: number,
  moduleId: number,
  quizId: number,
  userId: string
) => {
  const {
    data: quiz,
    isLoading,
    isError,
    error,
  } = useQuery<Quiz>({
    queryKey: ['quiz', courseId, moduleId, quizId, userId],
    queryFn: ({ signal }) =>
      CoursesService.getQuiz(courseId, moduleId, quizId, userId, signal),
    enabled: !!courseId && !!moduleId && !!quizId,
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

  return {
    quiz,
    isLoading,
    isError,
    error,
  };
};


export const useQuizAttempts = (
  courseId: number,
  moduleId: number,
  quizId: number,
  userId?: string
) => {
  const {
    data: attempts,
    isLoading,
    isError,
    error,
  } = useQuery<QuizAttempt[]>({
    queryKey: ['quiz-attempts', courseId, moduleId, quizId, userId],
    queryFn: ({ signal }) =>
      CoursesService.getQuizAttempts(courseId, moduleId, quizId, userId, signal),
    enabled: !!courseId && !!moduleId && !!quizId,
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

  return {
    attempts: attempts ?? [],
    isLoading,
    isError,
    error,
  };
};


export const useQuizAttemptById = (
  courseId: number,
  moduleId: number,
  quizId: number,
  attemptId: number,
  userId: string
) => {
  const {
    data: attempt,
    isLoading,
    isError,
    error,
  } = useQuery<QuizAttempt>({
    queryKey: ['quiz-attempt', courseId, moduleId, quizId, attemptId, userId],
    queryFn: ({ signal }) =>
      CoursesService.getQuizAttemptById(
        courseId,
        moduleId,
        quizId,
        attemptId,
        userId,
        signal
      ),
    enabled: !!courseId && !!moduleId && !!quizId && !!attemptId && !!userId,
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

  return {
    attempt,
    isLoading,
    isError,
    error,
  };
};


export const useSubmitQuizAttempt = () => {
  const queryClient = useQueryClient();

  return useMutation<
    QuizAttemptSubmission,
    unknown,
    {
      courseId: number;
      moduleId: number;
      quizId: number;
      payload: QuizAttemptSubmission;
    }
  >({
    mutationFn: ({ courseId, moduleId, quizId, payload }) =>
      CoursesService.submitQuizAttempt(courseId, moduleId, quizId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['quiz-attempts', variables.courseId, variables.moduleId, variables.quizId],
      });
      queryClient.invalidateQueries({
        queryKey: ['quiz', variables.courseId, variables.moduleId, variables.quizId],
      });
      queryClient.invalidateQueries({
        queryKey: ['quizzes', variables.courseId, variables.moduleId],
      });
    },
  });
};

